import { useMutation, useQueryClient } from '@tanstack/react-query';
import EventService, {
  ICreateEventParams,
} from '@clickersphere/core/services/EventService';
import { LeadQueryData, LEADS_QUERY_KEY } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { EventQueryData, EVENTS_QUERY_KEY } from './useEvents';

export function useCreateEvent() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: createEventIsPending, mutateAsync: createEventAsync } =
    useMutation({
      mutationFn: async (data: ICreateEventParams) => {
        const event = await EventService.create(data);
        return event as EventQueryData;
      },
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(EVENTS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        });

        const tempId = String(crypto.randomUUID());
        const newEvent: EventQueryData = {
          ...variables,
          id: tempId,
          queryStatus: 'pending',
        };

        queryClient.setQueryData<EventQueryData[]>(
          buildQueryKey(EVENTS_QUERY_KEY),
          (old) => [...(old ?? []).concat(newEvent)],
        );

        queryClient.setQueryData<LeadQueryData>(
          buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              events: [...(old.events ?? []).concat(newEvent)],
            };
          },
        );

        return { tempId };
      },
      onSuccess: async (data, variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(EVENTS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        });

        queryClient.setQueryData<EventQueryData[]>(
          buildQueryKey(EVENTS_QUERY_KEY),
          (old) =>
            old?.map((event) => (event.id === context.tempId ? data : event)),
        );

        queryClient.setQueryData<LeadQueryData>(
          buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              events: (old.events ?? []).map((event) =>
                event.id === context.tempId ? data : event,
              ),
            };
          },
        );
      },
      onError: async (_error, variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(EVENTS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        });

        queryClient.setQueryData<EventQueryData[]>(
          buildQueryKey(EVENTS_QUERY_KEY),
          (old) => old?.filter((event) => event.id !== context?.tempId),
        );
        queryClient.setQueryData<LeadQueryData>(
          buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
          (old) => {
            if (!old) return old;
            return {
              ...old,
              events: (old.events ?? []).filter(
                (event) => event.id !== context?.tempId,
              ),
            };
          },
        );
      },
    });

  return {
    createEventAsync,
    createEventIsPending,
  };
}
